import React, { useEffect } from 'react';
import classNames from 'classnames';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { logErrorBoundaryError } from 'lib/errors';
import { ErrorPlaceholder } from 'lib/errorBoundary';
import { getVehicleEventsPath } from 'lib/utils/store/vehicle';
import { getLocale } from 'lib/utils/getLocale';

import { IVehicle } from './types';
import { PriceEstimate } from './PriceEstimate';
import { AddPriceEstimateEvent } from './AddPriceEstimateEvent';
import { EstimateHeader } from './EstimateHeader';
import { NoEstimate } from './NoEstimate';
import { PriceCloudNew } from 'components/Vehicles/VehiclePriceEstimate/PriceCloudNew/PriceCloudNew';
import Link from 'components/Link';

import './vehicle-price-estimate-display-new.pcss';

export function VehiclePriceEstimateDisplayNew ({
    vehicle,
    notResponsive,
    priceCloudWithUpdate,
    onClose,
    onSuccess,
    displayAddPriceEstimateOnMount,
    displayCreateEventButton,
    eventCreated,
}: {
    vehicle: IVehicle,
    notResponsive?: boolean,
    priceCloudWithUpdate?: boolean,
    onClose?: () => void,
    onSuccess?: () => void,
    displayAddPriceEstimateOnMount?: boolean,
    displayCreateEventButton?: boolean,
    eventCreated?: boolean,
}) {
    const { t, i18n } = useTranslation('vehicles');

    const {
        estimatedAt
    } = vehicle;

    const displayClasses = classNames({
        'vehicle-price-estimate-display-new': true,
        'vehicle-price-estimate-display-new--not-responsive': notResponsive,
    });

    const showAddEvent = displayAddPriceEstimateOnMount && displayCreateEventButton;

    // TODO: remove this hook after VehiclePriceEstimateDisplay is removed
    useEffect(() => {
        const modal = document.querySelector('.react-modal');

        if (modal && modal.classList && !modal.classList.contains('baana-modal')) {
            modal.classList.add('baana-modal');
        }

        return () => {
            const modal = document.querySelector('.react-modal');

            if (modal && modal.classList && !modal.classList.contains('baana-modal')) {
                modal.classList.remove('baana-modal');
            }
        };
    }, []);

    if (!estimatedAt) {
        return <NoEstimate vehicle={vehicle} />;
    }

    return (
        <ErrorBoundary
            FallbackComponent={ErrorPlaceholder}
            onError={logErrorBoundaryError}
        >
            <div className={displayClasses}>
                <EstimateHeader vehicle={vehicle} />
                <PriceEstimate vehicle={vehicle} />
                {showAddEvent && (
                    <div className="vehicle-price-estimate-display-new__add-event">
                        <AddPriceEstimateEvent onSuccess={onSuccess} vehicle={vehicle} />
                    </div>
                )}
                {!showAddEvent && eventCreated && (
                    <div className="vehicle-price-estimate-display-new__event-added">
                        <Link
                            to={`${getLocale(i18n.language)}${getVehicleEventsPath(vehicle.id)}`}
                            trackName="gotoTimeline"
                        >
                            {t('common:gotoVehicleTimeline')}
                        </Link>
                    </div>
                )}
                {vehicle && (
                    <PriceCloudNew
                        vehicle={vehicle}
                        withUpdateLink={priceCloudWithUpdate}
                        onClose={onClose}
                    />
                )}
            </div>
        </ErrorBoundary>
    );
}
